'use client';

import clsx from 'clsx';
import React, { createContext, useContext, useState, useId } from 'react';

type TabsContextValue = {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  tabsId: string;
};

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext() {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tabs components must be used within a Tabs component');
  }
  return context;
}

type TabsProps = {
  defaultTab?: string;
  activeTab?: string;
  onChange?: (tab: string) => void;
  className?: string;
  children: React.ReactNode;
};

/**
 * Tabs component - manages tab state and provides context to child components
 *
 * @example
 * ```tsx
 * <Tabs defaultTab="details">
 *   <TabsList>
 *     <TabsButton tab="details">Details</TabsButton>
 *     <TabsButton tab="comments">Comments</TabsButton>
 *   </TabsList>
 *   <TabsContent tab="details">Details content</TabsContent>
 *   <TabsContent tab="comments">Comments content</TabsContent>
 * </Tabs>
 * ```
 */
export function Tabs({
  defaultTab = '',
  activeTab: controlledActiveTab,
  onChange,
  className,
  children,
}: TabsProps) {
  const tabsId = useId();
  const [internalActiveTab, setInternalActiveTab] = useState(defaultTab);
  const isControlled = controlledActiveTab !== undefined;
  const activeTab = isControlled ? controlledActiveTab : internalActiveTab;
  const setActiveTab = (tab: string) => {
    if (!isControlled) {
      setInternalActiveTab(tab);
    }
    onChange?.(tab);
  };

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab, tabsId }}>
      <div className={clsx(className)}>{children}</div>
    </TabsContext.Provider>
  );
}

type TabsListProps = {
  className?: string;
  children: React.ReactNode;
};

/**
 * Container for tab buttons
 */
export function TabsList({ className, children }: TabsListProps) {
  return (
    <div className={clsx('tw:border-b tw:border-zinc-200 tw:dark:border-zinc-700', className)}>
      <nav className="tw:-mb-px tw:flex tw:space-x-4" aria-label="Tabs">
        {children}
      </nav>
    </div>
  );
}

type TabsButtonProps = {
  tab: string;
  className?: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  badge?: React.ReactNode;
  dataTestId?: string;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'>;

/**
 * Individual tab button
 */
export function TabsButton({
  tab,
  className,
  children,
  icon,
  badge,
  dataTestId,
  ...props
}: TabsButtonProps) {
  const { activeTab, setActiveTab, tabsId } = useTabsContext();
  const isActive = activeTab === tab;
  const buttonId = `${tabsId}-${tab}`;

  return (
    <button
      {...props}
      id={buttonId}
      type="button"
      role="tab"
      data-testid={dataTestId}
      aria-selected={isActive}
      aria-controls={`${tabsId}-panel-${tab}`}
      onClick={() => setActiveTab(tab)}
      className={clsx(
        className,
        'tw:group tw:relative tw:whitespace-nowrap tw:border-b-3 tw:py-2 tw:px-3 tw:!text-sm tw:font-medium tw:transition-colors',
        'tw:focus:outline-none',
        isActive
          ? 'tw:border-brand-green-0 tw:text-brand-green-0 tw:dark:border-brand-green-1 tw:dark:text-brand-green-1'
          : 'tw:border-transparent tw:text-zinc-500 tw:hover:border-zinc-300 tw:hover:text-zinc-700 tw:dark:text-zinc-400 tw:dark:hover:border-zinc-600 tw:dark:hover:text-zinc-300',
        props.disabled && 'tw:opacity-50 tw:cursor-not-allowed'
      )}
    >
      <span className="tw:flex tw:items-center tw:gap-2">
        {icon && <span className="tw:flex-shrink-0">{icon}</span>}
        {children}
        {badge && (
          <span
            className={clsx(
              'tw:ml-0 tw:inline-flex tw:items-center tw:rounded-full tw:px-2.5 tw:py-0.5 tw:!text-xs tw:font-medium',
              isActive
                ? 'tw:bg-brand-green-2 tw:text-brand-green-0 tw:dark:bg-brand-green-1/30 tw:dark:text-brand-green-1'
                : 'tw:bg-zinc-200 tw:text-zinc-600 tw:dark:bg-zinc-800 tw:dark:text-zinc-400'
            )}
          >
            {badge}
          </span>
        )}
      </span>
    </button>
  );
}

type TabsContentProps = {
  tab: string;
  className?: string;
  children: React.ReactNode;
};

/**
 * Content panel for a specific tab - only renders when tab is active
 */
export function TabsContent({ tab, className, children }: TabsContentProps) {
  const { activeTab, tabsId } = useTabsContext();
  const isActive = activeTab === tab;

  if (!isActive) {
    return null;
  }

  return (
    <div
      id={`${tabsId}-panel-${tab}`}
      role="tabpanel"
      aria-labelledby={`${tabsId}-${tab}`}
      className={clsx('tw:mt-4', className)}
    >
      {children}
    </div>
  );
}

/**
 * Alternative: Simple hook-based approach for custom implementations
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { activeTab, setActiveTab } = useTabs('details');
 *
 *   return (
 *     <>
 *       <button onClick={() => setActiveTab('details')}>Details</button>
 *       <button onClick={() => setActiveTab('comments')}>Comments</button>
 *       {activeTab === 'details' && <DetailsView />}
 *       {activeTab === 'comments' && <CommentsView />}
 *     </>
 *   );
 * }
 * ```
 */
export function useTabs(defaultTab: string = '') {
  const [activeTab, setActiveTab] = useState(defaultTab);
  return { activeTab, setActiveTab };
}
